'use client';

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useLocationDetails } from '@/hooks/useLocation';
import { Button, TextField } from '@mui/material';
import TitleBar from '../layout/TitleBar';
import { MaterialReactTable } from 'material-react-table';
import { useNavigate } from 'react-router-dom';
import { createRoot } from 'react-dom/client';
import { useQueryReportsWithParams, useGenerateReport } from '@/hooks/useLocation';
import { lazy, Suspense } from 'react';

// Dynamically import the PDF component
const PDFGenerator = lazy(() => import('./PDFGenerator'));

const LocationReportTablePage: React.FC = () => {
  const { locationId } = useParams();
  const { data: locationDetails } = useLocationDetails(locationId || '');
  const location = locationDetails?.location;
  const [data, setData] = useState([] as any[]);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [sorting, setSorting] = useState([] as any[]);
  const [search, setSearch] = useState('');
  const [tableRefreshKey, setTableRefreshKey] = useState(0);
  const [debugHtml, setDebugHtml] = useState('');
  const [showDebugHtml, setShowDebugHtml] = useState(false);
  const navigate = useNavigate();
  const queryReportsMutation = useQueryReportsWithParams();
  const generateReportMutation = useGenerateReport();

  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'Service', href: '/app/location-service' },
    { label: location?.name || 'Location', href: `/app/location-service/${locationId}` },
    { label: 'Location Reports' },  // Current page
  ];

  const columns = [
    { accessorKey: 'id', header: 'ID' },
    { accessorKey: 'createdAt', header: 'Created At' },
    { accessorKey: 'updatedAt', header: 'Updated At' },
    {
      id: 'actions',
      header: 'Actions',
      Cell: ({ row }: any) => {
        console.log("THE F ROW", row)
        return (
          <div>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                console.log(row.original);
                // Generate fresh report HTML first
                generateReportMutation.mutate({
                  locationId: Number(locationId),
                  reportId: row.original.id,
                }, {
                  onSuccess: (data) => {
                    if (data && data.html) {
                      // Store HTML for debugging
                      setDebugHtml(data.html);
                      // setShowDebugHtml(true);
                      
                      // Handle PDF generation with fresh HTML
                      const pdfGenerator = document.createElement('div');
                      pdfGenerator.id = 'pdf-generator';
                      document.body.appendChild(pdfGenerator);
                      const root = createRoot(pdfGenerator);
                      root.render(<PDFGenerator html={data.html} locationId={locationId} />);
                      // Clean up after PDF generation
                      setTimeout(() => {
                        root.unmount();
                        document.body.removeChild(pdfGenerator);
                      }, 1000);
                    }
                  },
                  onError: (error) => {
                    console.error('Failed to generate report:', error);
                  }
                });
              }}
            >
              Download
            </Button>
            {/* <Button
              variant="outlined"
              size="small"
              style={{ marginLeft: '8px' }}
              onClick={() => {
                generateReportMutation.mutate({
                  locationId: Number(locationId),
                  reportId: row.original.id,
                }, {
                  onSuccess: (data) => {
                    if (data && data.html) {
                      setDebugHtml(data.html);
                      setShowDebugHtml(true);
                    }
                  },
                  onError: (error) => {
                    console.error('Failed to generate report:', error);
                  }
                });
              }}
            >
              Debug HTML
            </Button> */}
          </div>
        );
      },
    },
  ];

  const fetchData = async () => {
    setIsLoading(true);
    queryReportsMutation.mutate({
      locationId: Number(locationId),
      skip: pagination.pageIndex * pagination.pageSize,
      take: pagination.pageSize,
      search,
      orderBy: sorting[0]?.id || 'id',
      orderDirection: sorting[0]?.desc ? 'DESC' : 'ASC',
    }, {
      onSuccess: (result) => {
        setData(result.data || []);
        setTotalRows(result.total || 0);
        setIsLoading(false);
      },
      onError: (error) => {
        console.error('Failed to fetch reports:', error);
        setIsLoading(false);
      }
    });
  };

  useEffect(() => {
    fetchData();
  }, [pagination, sorting, search, tableRefreshKey]);

  return (
    <>
      <TitleBar
        breadcrumbs={breadcrumbs}
        title={`${location?.name || 'Location'} Reports`}
        rightContent={
          <Button
            variant="contained"
            onClick={() => navigate(`/app/location-service/${locationId}/reports/generate`)}
          >
            Generate Report
          </Button>
        }
      />

      <MaterialReactTable
        columns={columns}
        data={data}
        manualPagination
        manualSorting
        enableGlobalFilter={false}
        onPaginationChange={setPagination}
        onSortingChange={setSorting}
        rowCount={totalRows}
        state={{ isLoading, pagination, sorting }}
        initialState={{ density: 'compact' }}
        renderTopToolbarCustomActions={() => (
          <TextField
            placeholder="Search"
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: '300px' }}
          />
        )}
      />

      {/* Debug HTML Display */}
      {showDebugHtml && (
        <div style={{ marginTop: '20px', border: '1px solid #ccc', padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <h3>Debug HTML Preview</h3>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={() => setShowDebugHtml(false)}
            >
              Close
            </Button>
          </div>
          <div 
            dangerouslySetInnerHTML={{ __html: debugHtml }}
            style={{ 
              border: '1px solid #ddd', 
              padding: '20px', 
              backgroundColor: 'white',
              maxHeight: '600px',
              overflow: 'auto'
            }}
          />
        </div>
      )}
    </>
  );
};

export default LocationReportTablePage;
